import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, shareReplay } from 'rxjs';
import { makePermissionsComplete } from 'server/helpers/permissions/permissions';
import { AvailabilityStatusEnum } from 'server/shared/models/availabilityStatusEnum';
import { getObjectKeys } from 'server/shared/models/getObjectKeys';
import { RoleEnum } from 'server/shared/models/role.enum';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { PoolerUser, User } from 'server/shared/models/user';
import { UserAvailability } from 'server/shared/models/userAvailability';
import { PermissionsService } from '../../../services/permissions.service';
import { UserService } from '../../../services/user.service';
import { ShiftToggleEvent } from '../../../shared/modules/availability-shift-toggle-list/availability-shift-toggle-list.component';

export type EditAvailabilityDialogData = {
  availability: UserAvailability;
  user: PoolerUser;
};

@Component({
  selector: 'app-availability-edit-dialog',
  templateUrl: './availability-edit-dialog.component.html',
  styleUrls: ['./availability-edit-dialog.component.scss'],
})
export class AvailabilityEditDialogComponent implements OnInit {
  availabilityDate?: Date;
  shiftEnum = ShiftEnum;
  availabilityStatusEnum = AvailabilityStatusEnum;
  isEditingNote = false;
  noteText: string = '';

  canUpdateAvailabilities$ = this.userService.readyUser$.pipe(
    map((user: User) => {
      if (user.role === RoleEnum.DISPO) {
        let permissions = makePermissionsComplete(user.dispoPermissions);
        return permissions.poolerAccess.update;
      } else {
        return user._id === this.data.user._id;
      }
    }),
    shareReplay(1),
  );

  constructor(
    public dialogRef: MatDialogRef<AvailabilityEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditAvailabilityDialogData,
    public permissionsService: PermissionsService,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.noteText = this.data.availability.note || '';
    this.availabilityDate = new Date(this.data.availability.date);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  async toggleDayAvailability(available: boolean) {
    const backupState = {
      ...this.data.availability,
      shifts: { ...this.data.availability.shifts },
    };
    if (available === undefined) {
      this.data.availability.shifts = {};
      this.updateAvailabilityState();
    } else {
      if (
        !this.data.availability.shifts ||
        available == this.data.availability.isAvailable
      ) {
        this.data.availability.shifts = {};
      }
      const shiftKeyArray = Object.keys(ShiftEnum).filter(
        (key) => !isNaN(Number(key)),
      );
      if (available) {
        this.data.availability.isAvailable = true;
        shiftKeyArray.forEach((shift) => {
          if (
            //  @ts-ignore
            this.data.availability.shifts?.[shift] !==
              AvailabilityStatusEnum.WORKING &&
            this.data.availability.shifts
          ) {
            if (!this.data.user.shifts || this.data.user.shifts.length == 0) {
              //  @ts-ignore
              this.data.availability.shifts[shift] = 'AVAILABLE';
            } else if (
              this.data.user.shifts &&
              this.data.user.shifts.includes(Number.parseInt(shift, 10))
            ) {
              //  @ts-ignore
              this.data.availability.shifts[shift] = 'AVAILABLE';
            } else {
              //  @ts-ignore
              this.data.availability.shifts[shift] = 'UNAVAILABLE';
            }
          }
        });
      } else {
        this.data.availability.isAvailable = false;
        shiftKeyArray.forEach((shift) => {
          if (
            //  @ts-ignore
            this.data.availability.shifts?.[shift] !==
              AvailabilityStatusEnum.WORKING &&
            this.data.availability.shifts
          ) {
            //  @ts-ignore
            this.data.availability.shifts[shift] = 'UNAVAILABLE';
          }
        });
      }
    }
    const result = await this.updateAvailability();
    if (!result.availabilityUpdated) {
      this.data.availability = backupState;
    }
  }

  async toggleShift(toggleShiftEvent: ShiftToggleEvent) {
    const backupState = {
      ...this.data.availability,
      shifts: { ...this.data.availability.shifts },
    };
    if (!this.data.availability.shifts) {
      this.data.availability.shifts = {};
    }
    if (toggleShiftEvent.available === undefined) {
      delete this.data.availability.shifts[toggleShiftEvent.shift];
    } else if (toggleShiftEvent.available) {
      this.data.availability.shifts[toggleShiftEvent.shift] =
        AvailabilityStatusEnum.AVAILABLE;
    } else {
      this.data.availability.shifts[toggleShiftEvent.shift] =
        AvailabilityStatusEnum.UNAVAILABLE;
    }
    this.data.availability.isAvailable = this.hasAvailableShifts(
      this.data.availability.shifts,
    );
    const result = await this.updateAvailability();
    if (!result.availabilityUpdated) {
      this.data.availability = backupState;
    }
  }

  hasAvailableShifts(shifts: { [key in ShiftEnum]?: AvailabilityStatusEnum }) {
    const hasAvailable = getObjectKeys(shifts).some(
      (key) => shifts[key as ShiftEnum] == AvailabilityStatusEnum.AVAILABLE,
    );
    const hasUnavailable = getObjectKeys(shifts).some(
      (key) => shifts[key as ShiftEnum] == AvailabilityStatusEnum.UNAVAILABLE,
    );
    if (hasAvailable) {
      return true;
    }
    if (hasUnavailable) {
      return false;
    }
    return undefined;
  }

  updateAvailabilityState() {
    if (!this.data.availability.shifts) return;
    if (
      Object.keys(this.data.availability.shifts).some(
        (key) =>
          //  @ts-ignore
          this.data.availability.shifts![key] ==
          AvailabilityStatusEnum.AVAILABLE,
      )
    ) {
      this.data.availability.isAvailable = true;
    } else if (!Object.keys(this.data.availability.shifts).length) {
      delete this.data.availability.isAvailable;
    } else if (
      Object.keys(this.data.availability.shifts).every(
        (key) =>
          //  @ts-ignore
          this.data.availability.shifts![key] ==
          AvailabilityStatusEnum.UNAVAILABLE,
      )
    ) {
      this.data.availability.isAvailable = false;
    }
  }

  editNote(event: Event) {
    event.stopPropagation();
    this.isEditingNote = true;
  }

  saveNote(event: Event) {
    event.stopPropagation();
    this.isEditingNote = false;
    this.data.availability.note = this.noteText;
    this.updateAvailability();
  }

  private async updateAvailability() {
    return await this.userService.updateAvailability(
      this.data.user._id,
      this.data.availability,
    );
  }
}
